/**
 * Generate-Python viewer for the Agent Builder Build canvas.
 *
 * Ported from Agent Studio's components/CodePanel.tsx, restyled for factory-ui's
 * light theme. Shows the generated framework-free Python files as switchable
 * tabs with copy-to-clipboard and a lightweight regex Python highlighter
 * (the `.tok-*` classes live in styles/index.css).
 */

import { useEffect, useState } from 'react';
import type { GeneratedFile } from '../../types/agentBuilder';

// Lightweight Python highlighter — single regex pass with a replacer so we
// never re-scan inserted markup (avoids corrupting style attributes).
const KEYWORDS =
  'async|await|def|class|return|import|from|for|in|if|elif|else|try|except|finally|raise|with|as|None|True|False|self|yield|not|and|or|is|pass|break|continue|lambda|global|nonlocal';
const TOKEN = new RegExp(
  `(#[^\\n]*)` + // comments
    `|("""[\\s\\S]*?"""|'''[\\s\\S]*?'''|"(?:[^"\\\\]|\\\\.)*"|'(?:[^'\\\\]|\\\\.)*')` + // strings
    `|\\b(${KEYWORDS})\\b` + // keywords
    `|\\b(\\d+\\.?\\d*)\\b`, // numbers
  'g',
);

function highlight(code: string): string {
  const esc = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc.replace(TOKEN, (m, comment, str, kw, num) => {
    if (comment) return `<span class="tok-com">${comment}</span>`;
    if (str) return `<span class="tok-str">${str}</span>`;
    if (kw) return `<span class="tok-kw">${kw}</span>`;
    if (num) return `<span class="tok-num">${num}</span>`;
    return m;
  });
}

export function CodePanel({ files }: { files: GeneratedFile[] }) {
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (active >= files.length) setActive(0);
  }, [files, active]);

  if (!files.length)
    return <div className="p-6 text-[13px] text-neutral-500">No code generated yet.</div>;

  const file = files[Math.min(active, files.length - 1)];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(file.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard blocked in some sandboxes */
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="codegen-panel">
      <div className="flex items-center gap-1 overflow-x-auto border-b border-neutral-200 px-2 py-1.5">
        {files.map((f, i) => (
          <button
            key={f.path}
            onClick={() => setActive(i)}
            className={`whitespace-nowrap rounded-md px-2.5 py-1 text-[11px] transition-colors ${
              i === active
                ? 'bg-neutral-100 font-medium text-neutral-800'
                : 'text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700'
            }`}
          >
            {f.path}
          </button>
        ))}
        <button
          onClick={copy}
          className="ml-auto shrink-0 rounded-md border border-neutral-200 px-2.5 py-1 text-[11px] text-neutral-500 transition-colors hover:border-secondary-400 hover:text-neutral-800"
        >
          {copied ? 'copied ✓' : 'copy'}
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto bg-neutral-50">
        <pre className="p-4 font-mono text-[11.5px] leading-[1.55] text-neutral-700">
          <code dangerouslySetInnerHTML={{ __html: highlight(file.content) }} />
        </pre>
      </div>
    </div>
  );
}

export default CodePanel;
